// ═════════════════════════════════════════════════════════════════════════════
//  DOCUMENT URLS — one signer for the admin tab and the field page
// ═════════════════════════════════════════════════════════════════════════════
// `project-documents` is a PRIVATE bucket, so a document row's `file_url` is
// not something to render. It is empty on 164 of the 166 rows in production
// (only the storage PATH in `file_name` was ever written), and the two rows
// that do hold a URL hold an EXPIRED signed one. The path is the durable
// reference; the URL is derived per view and expires with it — which is also
// what a private bucket should hand out.
//
// The admin tab has done this since 2026-08-14. The field page never did, so
// every photo on a foreman's Documents screen rendered as a blank tile and
// every row opened nothing. Both call this now.
//
// Split into its own module so the two callers cannot drift, and so the
// batching rule below is testable without a browser.

export const DOC_URL_TTL = 60 * 60;   // one hour: long enough to read the page,
                                      // short enough that a copied link is not
                                      // a permanent door into a tenant's files.

// Signs a batch of document rows IN PLACE: each row's `file_url` becomes a
// fresh signed URL derived from its `file_name`.
//
// One round trip for the whole batch (createSignedUrls takes an array), and
// paths are de-duplicated first — the same drawing attached twice is one
// signature, not two.
//
// A row whose path cannot be signed keeps its stored link if that is plain https, so a file missing
// from storage shows up as one broken tile instead of an empty page.
//
// Returns the number of rows that got a fresh URL, which is what the callers'
// tests assert on.
export async function signDocumentRows(sbClient, rows, { ttl = DOC_URL_TTL, bucket = 'project-documents' } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  // A foreman can write any string into file_url, so every row keeps only a
  // plain https link — before signing, so a signing failure cannot skip it.
  list.forEach(r => { if (r && r.file_url) r.file_url = safeUrl(r.file_url); });
  const paths = [...new Set(list.map(r => r && r.file_name).filter(Boolean))];
  if (!paths.length || !sbClient) return 0;

  let data, error;
  try {
    ({ data, error } = await sbClient.storage.from(bucket).createSignedUrls(paths, ttl));
  } catch (e) {
    console.warn('document signing threw:', e && e.message);
    return 0;
  }
  if (error) { console.warn('document signing failed:', error.message); return 0; }

  const byPath = new Map();
  (data || []).forEach(d => { if (d && d.path && d.signedUrl) byPath.set(d.path, d.signedUrl); });

  let signed = 0;
  list.forEach(r => {
    if (r && byPath.has(r.file_name)) { r.file_url = byPath.get(r.file_name); signed++; }
  });
  return signed;
}

// Returns the URL only when it is a plain https link, else ''. Browsers drop
// tabs, newlines and control characters inside a scheme ("java\tscript:"), so
// those are removed before the check. The result still needs esc() when it
// goes into HTML.
export function safeUrl(u) {
  const s = String(u ?? '').replace(/[\u0000- \u007f]/g, '');
  if (!s) return '';
  try {
    return new URL(s).protocol === 'https:' ? s : '';
  } catch {
    return '';
  }
}

// Site photos are the reason the Documents page was slow to open: 133 of the
// 166 documents in production are photos, they were the FIRST section on the
// page, and each tile's `src` was the ORIGINAL file — 3.6 MB on average, 471 MB
// for the set. Nothing about that is a thumbnail.
//
// So the two pages now split the list here: documents render immediately,
// photos wait behind a folder the reader opens on purpose. Neither the
// signing round trip nor a single byte of image happens until then.
export const PHOTO_TYPE = 'site_photo';

export function splitPhotos(docs) {
  const list = Array.isArray(docs) ? docs : [];
  const photos = [], files = [];
  for (const d of list) ((d && d.type) === PHOTO_TYPE ? photos : files).push(d);
  return { photos, files };
}
